import type { TrackFunction } from '../../../../core/analytics/analyticsTypes';
import { toast } from '../../../../components/ui/toaster';
import type { StorageOperationError } from '../../../../types/azureStorage';
import { startQueueMessageDelete } from './queueApi';
import type { QueueActionSource } from './queueDialogs';
import type { QueueMessageTarget } from './queueMessageSelection';
import { useQueueMessagesStore } from './queueMessagesStore';

export const queueActionOutcome = (error: StorageOperationError | undefined): 'ok' | 'refused' | 'error' | 'cancelled' => {
  if (!error) return 'ok';
  if (error.category === 'cancelled') return 'cancelled';
  if (error.category === 'authorization' || error.category === 'invalid_input') return 'refused';
  return 'error';
};

export function trackQueueAction(track: TrackFunction, action: 'peek' | 'add' | 'receive' | 'update' | 'delete' | 'clear' | 'create_queue' | 'delete_queue', outcome: ReturnType<typeof queueActionOutcome>, source: QueueActionSource): void {
  track('feature:storage_queue_action', { action, outcome, source });
}

export async function deleteReceivedQueueMessage(args: {
  tabId: string; connectionId: string; target: QueueMessageTarget; source: QueueActionSource; track: TrackFunction;
}): Promise<void> {
  const { tabId, connectionId, target, source, track } = args;
  const store = useQueueMessagesStore.getState();
  // Latch (review Fable F3): a second click before the button re-renders disabled must not send the same receipt twice.
  if (store.byTab[tabId]?.pending.delete) return;
  store.setPending(tabId, 'delete', true);
  const result = await startQueueMessageDelete(tabId, { connectionId, receiptHandle: target.receiptHandle, timeoutMs: 30_000 }).promise;
  store.setPending(tabId, 'delete', false);
  if (result.ok) store.applyDelete(tabId, target.messageId);
  else {
    if (result.error.category === 'conflict' || result.error.category === 'not_found') store.markLost(tabId, target.messageId);
    toast.error({ title: 'Delete message failed', description: result.error.message });
  }
  trackQueueAction(track, 'delete', queueActionOutcome(result.ok ? undefined : result.error), source);
}
